import type {
  ActionResult,
  BundleDetail,
  BundleImportConfirmResult,
  BundleImportPreview,
  BundleInput,
  BundleListItem,
  BundleSaveResult,
  BundleTestBody,
  BundleTestResult,
  Paginated,
} from '@flux/shared';
import { apiFetch } from '@/lib/api/client.js';

/**
 * Couche réseau de l'écran Bundles (`02` §8). Les clés de requête vivent dans
 * `src/lib/query/keys.ts`.
 *
 * Les surcharges par boutique restent une notion serveur (`03` §2) mais ne
 * sont plus éditables depuis l'interface (décision du 10/09/2026) : un bundle
 * a une seule composition, la même pour toutes les origines.
 */
export interface BundleListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  search?: string;
  enabled?: boolean;
}

export function fetchBundlesList(params: BundleListParams): Promise<Paginated<BundleListItem>> {
  return apiFetch<Paginated<BundleListItem>>('/api/bundles', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.search ? { search: params.search } : {}),
      ...(params.enabled !== undefined ? { enabled: params.enabled } : {}),
    },
  });
}

export function fetchBundleDetail(sku: string): Promise<BundleDetail> {
  return apiFetch<BundleDetail>(`/api/bundles/${encodeURIComponent(sku)}`);
}

export function createBundle(body: BundleInput): Promise<BundleSaveResult> {
  return apiFetch<BundleSaveResult>('/api/bundles', { method: 'POST', body });
}

export function updateBundle(sku: string, body: BundleInput): Promise<BundleSaveResult> {
  return apiFetch<BundleSaveResult>(`/api/bundles/${encodeURIComponent(sku)}`, { method: 'PUT', body });
}

export function deleteBundle(sku: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/bundles/${encodeURIComponent(sku)}`, { method: 'DELETE' });
}

export interface PreviewBundleImportInput {
  content: string;
  format?: 'matrix' | 'simple';
  matrixMode?: 'components' | 'lookup';
}

export function previewBundleImport(body: PreviewBundleImportInput): Promise<BundleImportPreview> {
  return apiFetch<BundleImportPreview>('/api/bundles/import/preview', { method: 'POST', body });
}

/**
 * `items` doit être exactement `[...preview.created, ...preview.updated]`, non
 * reparsé côté client (contrat de `BundleImportConfirmBody`, `02` §8).
 */
export function confirmBundleImport(items: BundleInput[]): Promise<BundleImportConfirmResult> {
  return apiFetch<BundleImportConfirmResult>('/api/bundles/import/confirm', { method: 'POST', body: { items } });
}

export function testBundleExpansion(body: BundleTestBody): Promise<BundleTestResult> {
  return apiFetch<BundleTestResult>('/api/bundles/test', { method: 'POST', body });
}

/** Lit un `File` du navigateur en texte brut (contenu CSV de l'import bundles). */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new Error('Lecture du fichier impossible.'));
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Lecture du fichier impossible.'));
        return;
      }
      resolve(result);
    };
    reader.readAsText(file);
  });
}
